/**
 * Event door-prize drawing tab. Staff draw a random winner from everyone who redeemed a non-monetary
 * "Entry into a Drawing" reward at the redemption table. Winners are recorded (no re-draws of the same
 * person). Role-gating is enforced server-side by the draw_event_winner RPC.
 */
import { useEffect, useState } from "react";
import { GiftIcon } from "@heroicons/react/24/outline";
import {
  drawEventWinner,
  listDrawingWinners,
  type DrawnWinner,
  type DrawingWinnerRow,
} from "../../../services/eventDrawing.service";
import type { UUID } from "../../../types/common.types";

interface DrawingTabProps {
  eventId: UUID;
}

export default function DrawingTab({ eventId }: DrawingTabProps) {
  const [winners, setWinners] = useState<DrawingWinnerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawing, setDrawing] = useState(false);
  const [prizeNote, setPrizeNote] = useState("");
  const [latest, setLatest] = useState<DrawnWinner | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [emptyPool, setEmptyPool] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setWinners(await listDrawingWinners(eventId));
    } catch (e) {
      setError((e as Error)?.message ?? "Could not load winners");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const handleDraw = async () => {
    setDrawing(true);
    setError(null);
    setLatest(null);
    setEmptyPool(false);
    try {
      const drawn = await drawEventWinner(eventId, { prizeNote });
      if (drawn.length === 0) {
        setEmptyPool(true);
      } else {
        setLatest(drawn[0]);
        await load();
      }
    } catch (e) {
      setError((e as Error)?.message ?? "Could not draw a winner");
    } finally {
      setDrawing(false);
    }
  };

  const displayName = (name: string | null, email: string | null): string =>
    name?.trim() || email || "(no name on file)";

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          Door-prize drawing
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Pick a random winner from everyone who brought their QR code to the
          redemption table and redeemed their drawing entry. Each person can win
          only once; drawing again excludes prior winners.
        </p>
      </div>

      {/* Draw control */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <label
          htmlFor="prize-note"
          className="block text-sm font-medium text-gray-700"
        >
          Prize note (optional)
        </label>
        <input
          id="prize-note"
          type="text"
          value={prizeNote}
          onChange={(e) => setPrizeNote(e.target.value)}
          placeholder="e.g. Gift basket"
          className="mt-1 w-full max-w-sm rounded-md border border-gray-300 px-3 py-2 text-sm"
          disabled={drawing}
        />
        <div className="mt-3 flex items-center gap-3">
          <button
            type="button"
            onClick={handleDraw}
            disabled={drawing}
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <GiftIcon className="h-5 w-5" />
            {drawing ? "Drawing…" : "Draw a winner"}
          </button>
          {emptyPool && (
            <span className="text-sm text-amber-600">
              No one has redeemed a drawing entry for this event yet.
            </span>
          )}
          {error && <span className="text-sm text-red-600">{error}</span>}
        </div>

        {latest && (
          <div className="mt-4 rounded-md border border-green-200 bg-green-50 p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-green-700">
              Winner
            </p>
            <p className="mt-1 text-xl font-bold text-green-900">
              {displayName(latest.name_snapshot, latest.email_snapshot)}
            </p>
            {latest.email_snapshot && latest.name_snapshot && (
              <p className="text-sm text-green-700">{latest.email_snapshot}</p>
            )}
            <p className="mt-1 text-xs text-green-600">
              Drawn from {latest.pool_size ?? "?"} eligible
              {(latest.pool_size ?? 0) === 1 ? " person" : " people"}.
            </p>
          </div>
        )}
      </div>

      {/* Past winners */}
      <div>
        <h4 className="mb-2 text-sm font-semibold text-gray-700">
          Winners drawn {winners.length > 0 ? `(${winners.length})` : ""}
        </h4>
        {loading ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : winners.length === 0 ? (
          <p className="text-sm text-gray-400">No winners drawn yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Winner</th>
                  <th className="px-4 py-2 font-medium">Email</th>
                  <th className="px-4 py-2 font-medium">Prize</th>
                  <th className="px-4 py-2 font-medium">Drawn</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {winners.map((w) => (
                  <tr key={w.id}>
                    <td className="px-4 py-2 font-medium text-gray-800">
                      {displayName(w.name_snapshot, w.email_snapshot)}
                    </td>
                    <td className="px-4 py-2 text-gray-500">
                      {w.email_snapshot || "—"}
                    </td>
                    <td className="px-4 py-2 text-gray-500">
                      {w.prize_note || "—"}
                    </td>
                    <td className="px-4 py-2 text-gray-400">
                      {new Date(w.drawn_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
